import React, { useMemo } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell, ResponsiveContainer,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { daysInMonth } from "./utils";

// Build one datapoint per calendar day of the given month, with daily trade P&L
// and running cumulative P&L. Non-trade entries (deposit/withdraw) are ignored
// so the chart focuses on trading performance only.
function buildSeries(year, month, entries) {
  const total = daysInMonth(year, month);
  const perDay = new Map();
  for (const e of entries) {
    if (e.kind !== "trade") continue;
    const d = new Date(e.date);
    if (d.getFullYear() !== year || d.getMonth() !== month) continue;
    const day = d.getDate();
    perDay.set(day, (perDay.get(day) || 0) + e.amount);
  }
  const series = [];
  let cum = 0;
  for (let d = 1; d <= total; d++) {
    const daily = perDay.get(d) || 0;
    cum += daily;
    series.push({ day: d, daily: Number(daily.toFixed(2)), cumulative: Number(cum.toFixed(2)) });
  }
  return series;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const daily = payload.find((p) => p.dataKey === "daily")?.value ?? 0;
  const cum = payload.find((p) => p.dataKey === "cumulative")?.value ?? 0;
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-3 text-xs font-mono-plex">
      <div className="tracking-widest uppercase text-slate-500 mb-1">Day {label}</div>
      <div className={daily >= 0 ? "text-emerald-700" : "text-rose-700"}>
        Daily: {daily >= 0 ? "+" : ""}${daily.toFixed(2)}
      </div>
      <div className="text-[#00b4ff] mt-0.5">Cumulative: {cum >= 0 ? "+" : ""}${cum.toFixed(2)}</div>
    </div>
  );
}

const CHART_MARGIN = { top: 8, right: 12, left: -8, bottom: 0 };
const AXIS_TICK = { fontSize: 10, fill: "#64748b", fontFamily: "IBM Plex Mono, monospace" };
const AXIS_LINE = { stroke: "#e2e8f0" };
const TOOLTIP_CURSOR = { fill: "rgba(0, 180, 255, 0.05)" };
const BAR_RADIUS = [3, 3, 0, 0];
const ACTIVE_DOT = { r: 4, fill: "#00b4ff" };
const yTickFormatter = (v) => `$${v}`;

export default function JournalPnLChart({ year, month, entries }) {
  const data = useMemo(() => buildSeries(year, month, entries), [year, month, entries]);
  const finalCum = data.length ? data[data.length - 1].cumulative : 0;
  const bestDay = useMemo(() => data.reduce((b, r) => (r.daily > (b?.daily ?? -Infinity) ? r : b), null), [data]);
  const worstDay = useMemo(() => data.reduce((w, r) => (r.daily < (w?.daily ?? Infinity) ? r : w), null), [data]);
  const tradingDays = data.filter((r) => r.daily !== 0).length;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 mb-6" data-testid="journal-pnl-chart">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono-plex tracking-[0.28em] uppercase text-slate-500">Monthly P&L</div>
          <h3 className="mt-1 font-display text-lg md:text-xl font-bold tracking-tight">Daily performance</h3>
          <p className="text-[11px] font-mono-plex tracking-widest text-slate-400 mt-0.5">
            Bars = daily net · Line = cumulative
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500">Month end</div>
            <div className={`font-display font-bold text-xl md:text-2xl tracking-tight ${finalCum >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {finalCum >= 0 ? "+" : ""}${finalCum.toFixed(2)}
            </div>
          </div>
          <TrendingUp size={18} className="text-[#00b4ff]" />
        </div>
      </div>

      {tradingDays === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
          Belum ada trade entry untuk bulan ini. Chart akan muncul bila anda tambah entry.
        </div>
      ) : (
        <>
          <div className="w-full h-56 md:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="day" tick={AXIS_TICK} tickLine={false} axisLine={AXIS_LINE} />
                <YAxis tick={AXIS_TICK} tickLine={false} axisLine={AXIS_LINE} tickFormatter={yTickFormatter} />
                <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR} />
                <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="2 2" />
                <Bar dataKey="daily" radius={BAR_RADIUS} maxBarSize={22}>
                  {data.map((row) => (
                    <Cell
                      key={`cell-${row.day}`}
                      fill={row.daily >= 0 ? "#10b981" : "#f43f5e"}
                    />
                  ))}
                </Bar>
                <Line
                  type="monotone"
                  dataKey="cumulative"
                  stroke="#00b4ff"
                  strokeWidth={2}
                  dot={false}
                  activeDot={ACTIVE_DOT}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-lg bg-slate-50 border border-slate-200 py-2.5 px-3">
              <div className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">Trading days</div>
              <div className="mt-1 font-display font-bold text-lg">{tradingDays}</div>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 py-2.5 px-3">
              <div className="text-[9px] font-mono-plex tracking-widest uppercase text-emerald-700">Best day</div>
              <div className="mt-1 font-display font-bold text-lg text-emerald-700">
                {bestDay && bestDay.daily > 0 ? `+$${bestDay.daily.toFixed(2)}` : "—"}
              </div>
              {bestDay && bestDay.daily > 0 && (
                <div className="text-[9px] font-mono-plex tracking-widest text-emerald-600/70 mt-0.5">Day {bestDay.day}</div>
              )}
            </div>
            <div className="rounded-lg bg-rose-50 border border-rose-200 py-2.5 px-3">
              <div className="text-[9px] font-mono-plex tracking-widest uppercase text-rose-700">Worst day</div>
              <div className="mt-1 font-display font-bold text-lg text-rose-700">
                {worstDay && worstDay.daily < 0 ? `-$${Math.abs(worstDay.daily).toFixed(2)}` : "—"}
              </div>
              {worstDay && worstDay.daily < 0 && (
                <div className="text-[9px] font-mono-plex tracking-widest text-rose-600/70 mt-0.5">Day {worstDay.day}</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
